import React, { useRef, useEffect, useCallback } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import Overlay from "../../../Games/Shared/Overlay/Overlay";
import HoldAndWinReloadController from "./HoldAndWinReloadController";
import { safeInit } from "../../../utilities";
import {
  selectHoldAndWinResumePopupActive,
  selectRespinsLeft,
} from "../../../State/selectors";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

/**
 * HoldAndWinReloadDialog - Shows when user reloads during active Hold and Win
 * Displays remaining respins and allows user to continue the feature
 */
const HoldAndWinReloadDialog = () => {
  const dispatch = useDispatch();
  const showReloadDialog = useSelector(selectHoldAndWinResumePopupActive);
  const respinsLeft = useSelector(selectRespinsLeft);

  const reloadControllerRef = useRef(null);
  const spineContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight } = dimensionsContext;

  // Initialize controller when dialog becomes active and start animation immediately
  useEffect(() => {
    if (
      showReloadDialog &&
      spineContainerRef.current &&
      !reloadControllerRef.current
    ) {
      console.log("🟠 [HoldAndWinReloadDialog] Initializing controller with respinsLeft:", respinsLeft);
      safeInit(() => {
        reloadControllerRef.current = new HoldAndWinReloadController(
          spineContainerRef.current,
          dimensionsContext,
          dispatch,
          symbolHeight,
          overlayRef,
          respinsLeft
        );

        // Start animation immediately after initialization
        if (reloadControllerRef.current) {
          reloadControllerRef.current.startSequence();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReloadDialog]);

  // Handle dimension changes (only when dialog is active)
  useEffect(() => {
    if (
      showReloadDialog &&
      reloadControllerRef.current &&
      symbolHeight &&
      dimensionsContext
    ) {
      reloadControllerRef.current.updateDimensions(
        dimensionsContext,
        symbolHeight
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReloadDialog, symbolHeight]);

  // Handle user click to continue animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (reloadControllerRef.current && showReloadDialog) {
        const accepted = reloadControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showReloadDialog]
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when dialog is showing AND controller exists
      if (!showReloadDialog || !reloadControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = reloadControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        console.error("[HoldAndWinReloadDialog] Spacebar error:", error);
        // Emergency recovery - try to finish animation
        if (
          reloadControllerRef.current &&
          reloadControllerRef.current.finishAnimation
        ) {
          reloadControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (showReloadDialog) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showReloadDialog]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reloadControllerRef.current) {
        reloadControllerRef.current.cleanup();
      }
    };
  }, []);

  // Don't render anything if dialog is not active
  if (!showReloadDialog) {
    return null;
  }

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_reload_dialog"
      x={dimensions.width / 2}
      y={dimensions.height / 2}
      sortableChildren={true}
      zIndex={9}
      ref={spineContainerRef}
    >
      <Overlay
        ref={overlayRef}
        dimensions={dimensions}
        interactive={showReloadDialog}
        onPointerDown={handleBackgroundClick}
        x={-dimensions.width / 2}
        y={-dimensions.height / 2}
        fadeDuration={350}
      />
    </Container>
  );
};

export default HoldAndWinReloadDialog;
